import React, { useEffect, useRef } from 'react';
import Plotly from 'plotly.js';
import { plotlyResizeIfDisplayed } from '../utils/plotlySafeResize';
import './JackAdvancedHistograms.css';

/** Match JackSyncedPlotlyCharts / suspension panel: front blue, rear pink. */
const COLOR_FRONT_COMP = '#42a5f5';
const COLOR_FRONT_REB = '#64b5f6';
const COLOR_REAR_COMP = '#ec4899';
const COLOR_REAR_REB = '#f472b6';
const COLOR_FRONT_POS = '#42a5f5';
const COLOR_REAR_POS = '#ec4899';

const plotlyConfig = {
  responsive: true,
  displayModeBar: false,
};

const MAX_POSITION_HIST_SAMPLES = 6000;
const MAX_STROKE_HIST_SAMPLES = 5000;

function decimateValues(values, maxSamples) {
  if (!Array.isArray(values)) return [];
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length <= maxSamples) return finite;
  const step = Math.ceil(finite.length / maxSamples);
  const sampled = [];
  for (let i = 0; i < finite.length; i += step) sampled.push(finite[i]);
  return sampled;
}

function axisDark() {
  return {
    title: { font: { color: '#9aa0a6' } },
    gridcolor: 'rgba(255, 255, 255, 0.08)',
    zerolinecolor: 'rgba(255, 255, 255, 0.12)',
    color: '#888',
    linecolor: 'rgba(255, 255, 255, 0.15)',
  };
}

function baseDarkLayout() {
  return {
    paper_bgcolor: '#0a0a0a',
    plot_bgcolor: '#121212',
    font: { color: '#9aa0a6', size: 11 },
    bargap: 0.05,
    hovermode: 'closest',
  };
}

/** Same as jack-benchmark.html renderStrokeHistograms() plotHistogram(). */
function plotStrokeHistogram(el, data, xLabel, color) {
  if (!el) return;
  Plotly.purge(el);
  el.innerHTML = '';
  if (!data || data.length === 0) {
    el.innerHTML = '<p class="jack-adv-hist-inline-empty">No data</p>';
    return;
  }
  const nbinsx = Math.min(20, Math.max(5, Math.ceil(data.length / 5)));
  const trace = {
    x: data,
    type: 'histogram',
    marker: { color },
    nbinsx,
  };
  const axis = axisDark();
  const layout = {
    ...baseDarkLayout(),
    margin: { t: 10, r: 20, b: 44, l: 50 },
    xaxis: { ...axis, title: { text: xLabel, ...axis.title } },
    yaxis: { ...axis, title: { text: 'Count', ...axis.title } },
  };
  Plotly.newPlot(el, [trace], layout, plotlyConfig);
}

/**
 * Same as jack-benchmark.html renderPositionHistograms()
 * (engine uses d.front / d.rear mm; benchmark HTML referenced frontMM incorrectly).
 */
function plotPositionHistogram(el, positions, totalTravel, color) {
  if (!el) return;
  Plotly.purge(el);
  el.innerHTML = '';
  if (!positions || positions.length === 0 || !Number.isFinite(totalTravel) || totalTravel <= 0) {
    el.innerHTML = '<p class="jack-adv-hist-inline-empty">No data</p>';
    return;
  }
  const numBins = 20;
  const binSize = totalTravel / numBins;
  const counts = new Array(numBins).fill(0);
  positions.forEach((pos) => {
    if (!Number.isFinite(pos)) return;
    const binIndex = Math.min(Math.floor(pos / binSize), numBins - 1);
    counts[binIndex]++;
  });
  const bins = [];
  for (let i = 0; i < numBins; i++) {
    const binStart = i * binSize;
    const binEnd = (i + 1) * binSize;
    const binCenter = (binStart + binEnd) / 2;
    const percent = ((binCenter / totalTravel) * 100).toFixed(0);
    bins.push({
      x: binCenter,
      y: counts[i],
      label: `${binCenter.toFixed(0)} mm (${percent}%)`,
    });
  }
  const axis = axisDark();
  const trace = {
    x: bins.map((b) => b.x),
    y: bins.map((b) => b.y),
    type: 'bar',
    marker: { color },
    hovertemplate: '%{text}<br>Count: %{y}<extra></extra>',
    text: bins.map((b) => b.label),
  };
  const layout = {
    ...baseDarkLayout(),
    margin: { t: 10, r: 20, b: 48, l: 56 },
    xaxis: {
      ...axis,
      title: { text: 'Position (mm)', ...axis.title },
      range: [0, totalTravel],
    },
    yaxis: { ...axis, title: { text: 'Sample count', ...axis.title } },
  };
  Plotly.newPlot(el, [trace], layout, plotlyConfig);
}

function purgeEl(el) {
  if (!el) return;
  try {
    Plotly.purge(el);
  } catch (_) {
    /* ignore */
  }
  el.innerHTML = '';
}

/**
 * Jack benchmark Advanced tab: axle position (20 bins) + stroke depth / 95th-pct-velocity histograms.
 * Two columns: front | rear. Same stroke stats as jack-benchmark (allStrokes from jackPackingEngine).
 */
export default function JackAdvancedHistograms({
  processedData = [],
  analysisResults = null,
  jackOk = false,
  view = 'all',
}) {
  const wrapRef = useRef(null);
  const frontPosRef = useRef(null);
  const frontCompDepthRef = useRef(null);
  const frontCompVelRef = useRef(null);
  const frontRebDepthRef = useRef(null);
  const frontRebVelRef = useRef(null);
  const rearPosRef = useRef(null);
  const rearCompDepthRef = useRef(null);
  const rearCompVelRef = useRef(null);
  const rearRebDepthRef = useRef(null);
  const rearRebVelRef = useRef(null);

  const allPlotRefs = [
    frontPosRef,
    frontCompDepthRef,
    frontCompVelRef,
    frontRebDepthRef,
    frontRebVelRef,
    rearPosRef,
    rearCompDepthRef,
    rearCompVelRef,
    rearRebDepthRef,
    rearRebVelRef,
  ];

  useEffect(() => {
    allPlotRefs.forEach((r) => purgeEl(r.current));

    if (!jackOk || !analysisResults?.stats) {
      return () => {
        allPlotRefs.forEach((r) => purgeEl(r.current));
      };
    }

    const params = analysisResults.params || {};
    const frontTravel =
      Number.isFinite(Number(params.frontTravel)) && Number(params.frontTravel) > 0
        ? Number(params.frontTravel)
        : 200;
    const rearTravel =
      Number.isFinite(Number(params.rearTravel)) && Number(params.rearTravel) > 0
        ? Number(params.rearTravel)
        : 210;

    const hasSamples = Array.isArray(processedData) && processedData.length > 0;
    const frontPositions = hasSamples
      ? decimateValues(processedData.map((d) => d.front), MAX_POSITION_HIST_SAMPLES)
      : [];
    const rearPositions = hasSamples
      ? decimateValues(processedData.map((d) => d.rear), MAX_POSITION_HIST_SAMPLES)
      : [];

    plotPositionHistogram(frontPosRef.current, frontPositions, frontTravel, COLOR_FRONT_POS);
    plotPositionHistogram(rearPosRef.current, rearPositions, rearTravel, COLOR_REAR_POS);

    const frontVel = analysisResults.stats.front?.velocity;
    const rearVel = analysisResults.stats.rear?.velocity;

    const compF = frontVel?.allStrokes?.compression;
    const rebF = frontVel?.allStrokes?.rebound;
    const compR = rearVel?.allStrokes?.compression;
    const rebR = rearVel?.allStrokes?.rebound;

    plotStrokeHistogram(
      frontCompDepthRef.current,
      decimateValues(compF?.map((s) => s.depth), MAX_STROKE_HIST_SAMPLES),
      'Stroke depth (mm)',
      COLOR_FRONT_COMP
    );
    plotStrokeHistogram(
      frontCompVelRef.current,
      decimateValues(compF?.map((s) => s.p95Velocity ?? s.maxVelocity), MAX_STROKE_HIST_SAMPLES),
      '95th pct velocity (mm/s)',
      COLOR_FRONT_COMP
    );
    plotStrokeHistogram(
      frontRebDepthRef.current,
      decimateValues(rebF?.map((s) => s.depth), MAX_STROKE_HIST_SAMPLES),
      'Stroke depth (mm)',
      COLOR_FRONT_REB
    );
    plotStrokeHistogram(
      frontRebVelRef.current,
      decimateValues(rebF?.map((s) => s.p95Velocity ?? s.maxVelocity), MAX_STROKE_HIST_SAMPLES),
      '95th pct velocity (mm/s)',
      COLOR_FRONT_REB
    );

    plotStrokeHistogram(
      rearCompDepthRef.current,
      decimateValues(compR?.map((s) => s.depth), MAX_STROKE_HIST_SAMPLES),
      'Stroke depth (mm)',
      COLOR_REAR_COMP
    );
    plotStrokeHistogram(
      rearCompVelRef.current,
      decimateValues(compR?.map((s) => s.p95Velocity ?? s.maxVelocity), MAX_STROKE_HIST_SAMPLES),
      '95th pct velocity (mm/s)',
      COLOR_REAR_COMP
    );
    plotStrokeHistogram(
      rearRebDepthRef.current,
      decimateValues(rebR?.map((s) => s.depth), MAX_STROKE_HIST_SAMPLES),
      'Stroke depth (mm)',
      COLOR_REAR_REB
    );
    plotStrokeHistogram(
      rearRebVelRef.current,
      decimateValues(rebR?.map((s) => s.p95Velocity ?? s.maxVelocity), MAX_STROKE_HIST_SAMPLES),
      '95th pct velocity (mm/s)',
      COLOR_REAR_REB
    );

    const root = wrapRef.current;
    const onResize = () => {
      if (!root) return;
      root.querySelectorAll('.js-plotly-plot').forEach((el) => {
        plotlyResizeIfDisplayed(el);
      });
    };
    let ro = null;
    if (root) {
      ro = new ResizeObserver(onResize);
      ro.observe(root);
      window.addEventListener('resize', onResize);
      requestAnimationFrame(onResize);
    }

    return () => {
      if (ro) {
        ro.disconnect();
        window.removeEventListener('resize', onResize);
      }
      allPlotRefs.forEach((r) => purgeEl(r.current));
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- allPlotRefs are stable useRef identities
  [jackOk, processedData, analysisResults]);

  if (!jackOk || !analysisResults?.stats) {
    return null;
  }

  const showPosition = view === 'all' || view === 'position';
  const showVelocity = view === 'all' || view === 'velocity';
  const showStroke = view === 'all' || view === 'stroke';

  return (
    <div ref={wrapRef} className="jack-adv-hist-root">
      {showPosition ? (
        <>
          <h4 className="jack-adv-hist-section-title">Position distribution</h4>
          <div className="jack-adv-hist-two-col">
            <div className="jack-adv-hist-col jack-adv-hist-col--front">
              <h5 className="jack-adv-hist-col-title">Front</h5>
              <div className="jack-adv-hist-chart-title">Suspension position</div>
              <div ref={frontPosRef} className="jack-adv-hist-plot jack-adv-hist-plot--tall" />
            </div>
            <div className="jack-adv-hist-col jack-adv-hist-col--rear">
              <h5 className="jack-adv-hist-col-title">Rear</h5>
              <div className="jack-adv-hist-chart-title">Suspension position</div>
              <div ref={rearPosRef} className="jack-adv-hist-plot jack-adv-hist-plot--tall" />
            </div>
          </div>
        </>
      ) : null}

      {showVelocity ? (
        <>
          <h4 className={`jack-adv-hist-section-title${showPosition ? ' jack-adv-hist-section-title--spaced' : ''}`}>
            Velocity distribution
          </h4>
          <div className="jack-adv-hist-two-col">
            <div className="jack-adv-hist-col jack-adv-hist-col--front">
              <h5 className="jack-adv-hist-col-title">Front</h5>
              <div className="jack-adv-hist-chart-title">Compression velocity</div>
              <div ref={frontCompVelRef} className="jack-adv-hist-plot" />
              <div className="jack-adv-hist-chart-title">Rebound velocity</div>
              <div ref={frontRebVelRef} className="jack-adv-hist-plot" />
            </div>
            <div className="jack-adv-hist-col jack-adv-hist-col--rear">
              <h5 className="jack-adv-hist-col-title">Rear</h5>
              <div className="jack-adv-hist-chart-title">Compression velocity</div>
              <div ref={rearCompVelRef} className="jack-adv-hist-plot" />
              <div className="jack-adv-hist-chart-title">Rebound velocity</div>
              <div ref={rearRebVelRef} className="jack-adv-hist-plot" />
            </div>
          </div>
        </>
      ) : null}

      {showStroke ? (
        <>
          <h4 className={`jack-adv-hist-section-title${showPosition || showVelocity ? ' jack-adv-hist-section-title--spaced' : ''}`}>
            Stroke length distribution
          </h4>
          <div className="jack-adv-hist-two-col">
            <div className="jack-adv-hist-col jack-adv-hist-col--front">
              <h5 className="jack-adv-hist-col-title">Front</h5>
              <div className="jack-adv-hist-chart-title">Compression stroke depth</div>
              <div ref={frontCompDepthRef} className="jack-adv-hist-plot" />
              <div className="jack-adv-hist-chart-title">Rebound stroke depth</div>
              <div ref={frontRebDepthRef} className="jack-adv-hist-plot" />
            </div>
            <div className="jack-adv-hist-col jack-adv-hist-col--rear">
              <h5 className="jack-adv-hist-col-title">Rear</h5>
              <div className="jack-adv-hist-chart-title">Compression stroke depth</div>
              <div ref={rearCompDepthRef} className="jack-adv-hist-plot" />
              <div className="jack-adv-hist-chart-title">Rebound stroke depth</div>
              <div ref={rearRebDepthRef} className="jack-adv-hist-plot" />
            </div>
          </div>
        </>
      ) : null}

      {view === 'all' ? (
        <div className="jack-adv-hist-all-note">
          Position, velocity, and stroke-length panels are available separately from the panel picker.
        </div>
      ) : null}
    </div>
  );
}
